function loadImg(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function (err) {
      console.log(err);
      reject(err);
    };
  });
}

function loadImageSequence(
    base: string,
    extension: string,
    fps: number
): Promise<HTMLImageElement[]> {
  return Promise.all(
      Array(fps)
          .fill(true)
          .map((i, index) =>
              loadImg(`${base}/${index.toString().padStart(3, "0")}.${extension}`)
          )
  );
}

function buildLoadingSequence(
    fps: number,
    initialSequence: number[] = [0]
): number[] {
  let factor;
  let newIndex;
  let startIndex = initialSequence[initialSequence.length - 1];
  const sequence: number[] = [...initialSequence];
  for (let i = 3; i >= 0; i--) {
    factor = Math.pow(2, i);
    for (let j = 0; j < fps; j += factor) {
      newIndex = ((j % 2 ? j : -j) + startIndex + fps) % fps;
      if (newIndex !== startIndex && !sequence.includes(newIndex)) {
        sequence.push(newIndex);
      }
    }
  }
  return sequence;
}

interface ImageSequenceDefinition {
  loDef: string;
  hiDef: string;
  fullpath: string;
  hasAlphas: boolean;
  fps: number;
}

type Frame = { img: HTMLImageElement; alpha: HTMLImageElement };

class ImageProvider {
  private loDef: string;
  private hiDef: string;
  private hasAlphas: boolean;
  private fps: number;
  private path: string;
  private loSeq: Frame[] = [];
  private hiSeq: Frame[] = [];
  private onHdReady: (frame: Frame, index: number) => void;
  private loadingQueue: number[] = [];
  private hdTimer: any;
  private index: number = null;

  async setScene(
      scene: ImageSequenceDefinition,
      frameIndex: number,
      resoveIndex: number = 0
  ) {
    this.loDef = scene.loDef;
    this.hiDef = scene.hiDef;
    this.index = null;
    this.hasAlphas = scene.hasAlphas;
    this.fps = scene.fps;
    this.path = scene.fullpath;
    if (this.hdTimer) {
      clearTimeout(this.hdTimer);
    }
    this.hiSeq = [];
    this.loSeq = [];
    return await this.startLoading(frameIndex, resoveIndex);
  }

  setHdReadyHandler(handler: (frame: Frame, index: number) => void): void {
    this.onHdReady = handler;
  }

  async loadFrame(index: number, definition: string, cache: string) {
    const extension = definition.split("-")[0];
    const promises = [
      loadImg(
          `${this.path}/${definition}/${index
              .toString()
              .padStart(3, "0")}.${extension}`
      ),
    ];
    if (this.hasAlphas) {
      promises.push(
          loadImg(`${this.path}/alpha/${index.toString().padStart(3, "0")}.png`)
      );
    }
    return Promise.all(promises).then(([img, alpha]) => {
      const result = {img, alpha};
      if (cache === "lo") {
        this.loSeq[index] = result;
      } else if (cache === "hi") {
        this.hiSeq[index] = result;
      }
      return result;
    });
  }

  startLoadingQueue(resolveIndex: number = 0) {
    if (!this.loadingQueue.length) {
      return;
    }
    const promise1 = Promise.all(
        this.loadingQueue
            .slice(0, resolveIndex)
            .map((index) => this.loadFrame(index, this.loDef, "lo"))
    );
    const promise2 = Promise.all(
        this.loadingQueue
            .slice(resolveIndex, this.loadingQueue.length)
            .map((index) => this.loadFrame(index, this.loDef, "lo"))
    );
    return promise1.then(() => promise2);
  }

  async startLoading(index: number, resolveIndex: number = 0) {
    this.loSeq.length = 0;
    this.hiSeq.length = 0;
    this.loadingQueue.length = 0;

    let sequence = [index];
    for (let i = 1; i < resolveIndex; i++) {
      sequence.push((i + index) % this.fps);
    }
    this.loadingQueue = buildLoadingSequence(this.fps, sequence);
    return this.startLoadingQueue(resolveIndex);
  }

  getFrame(index: number): Frame {
    let result = this.hiSeq[index]?.img.src.startsWith(this.path) ?
        this.hiSeq[index] : this.loSeq[index]?.img.src.startsWith(this.path) ? this.loSeq[index] : null;
    if (result && index !== this.index) {
      if (this.hdTimer) {
        clearTimeout(this.hdTimer);
      }
      this.hdTimer = setTimeout(() => {
        if (
            this.hiSeq[index] &&
            this.hiSeq[index].img.src.startsWith(this.path)
        ) {
          this.onHdReady(this.hiSeq[index], index);
        } else {
          this.loadFrame(index, this.hiDef, "hi").then((frame) => {
            if (frame.img.src.startsWith(this.path)) {
              this.hiSeq[index] = frame;
              this.onHdReady(frame, index);
            }
          });
        }
      }, 500);
      this.index = index;
    }
    return result;
  }
}

export {ImageProvider, ImageSequenceDefinition, Frame, loadImg};
